import { logger } from '~/utils/logger';

import type { Organization, OrganizationMember, OrgRole, UpdateRoleRequest } from '~/types';

import { apiRequest } from './client';
import { ENDPOINTS, HTTP_METHODS } from './config';

interface CreateOrganizationPayload {
  name: string;
  slug: string;
  description?: string;
}

interface UpdateOrganizationPayload {
  name?: string;
  description?: string;
}

export interface DomainListResponse {
  domains: OrgDomain[];
  total_count: number;
}

export interface EnrollExistingUsersResponse {
  domain: string;
  enrolled: number;
  skipped_already_member: number;
  skipped_pending: number;
  re_enrolled: number;
}

export interface EnrollmentPreviewResponse {
  domain: string;
  governance_policy: string;
  users_to_enroll: EnrollmentPreviewUser[];
  already_members: number;
  pending_consent: number;
  pagination: PaginationMeta;
}

export interface EnrollmentPreviewUser {
  uid: string;
  email: string;
  display_name: string | null;
}

export interface OrgDomain {
  id: string;
  organization_id: string;
  domain: string;
  status: 'pending_verification' | 'verified' | 'revoked';
  governance_policy: 'none' | 'auto_enroll';
  verification_token: string | null;
  verification_token_expires_at: string | null;
  verified_at: string | null;
  verify_attempt_count: number;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaginationMeta {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// =============================================================================
// Member Management API (Story 5.5)
// =============================================================================

export interface VerifyDomainResponse {
  success: boolean;
  domain: OrgDomain;
  message: string;
}

/**
 * Send an invitation to join an organization.
 *
 * @param orgId - Organization ID
 * @param email - Email address to invite
 * @param role - Role to assign (admin or member)
 */
export async function createInvitation(
  orgId: string,
  email: string,
  role: Exclude<OrgRole, 'owner'>,
): Promise<void> {
  await apiRequest(`${ENDPOINTS.ORGANIZATIONS}/${orgId}/invitations`, {
    method: HTTP_METHODS.POST,
    body: JSON.stringify({ email, role }),
  });
}

/**
 * Create a new organization.
 * The authenticated user becomes the owner.
 *
 * @throws ApiError with status 409 if slug is already taken
 */
export async function createOrganization(data: CreateOrganizationPayload): Promise<Organization> {
  const response = await apiRequest<Organization>(ENDPOINTS.ORGANIZATIONS, {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(data),
  });

  return response.data;
}

// ---------------------------------------------------------------------------
// Domain Governance API
// ---------------------------------------------------------------------------

/**
 * Delete an organization permanently.
 * Requires owner role and slug confirmation via X-Confirm-Delete header.
 *
 * @param orgId - Organization ID
 * @param confirmSlug - Organization slug for confirmation (must match exactly)
 */
export async function deleteOrganization(orgId: string, confirmSlug: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.ORGANIZATIONS}/${orgId}`, {
    method: HTTP_METHODS.DELETE,
    headers: {
      'X-Confirm-Delete': confirmSlug,
    },
  });
}

/**
 * Get the caller's last authorized organization ID, to pre-select it on the OAuth
 * consent screen. Best-effort: returns null (and logs) on any failure — a lookup
 * hiccup here must never block the consent flow, so it never throws.
 */
export async function getLastOrganization(): Promise<string | null> {
  try {
    const response = await apiRequest<{ organization_id: string | null }>(
      '/oauth/last-organization',
    );
    return response.data.organization_id;
  } catch (err) {
    logger.warn(
      { component: 'organizations.getLastOrganization' },
      'Failed to fetch last authorized organization',
      { error: err instanceof Error ? err.message : String(err) },
    );
    return null;
  }
}

/**
 * Get organizations for the current authenticated user.
 * Returns only organizations where user has active membership.
 */
export async function getMyOrganizations(): Promise<Organization[]> {
  const response = await apiRequest<Organization[]>(ENDPOINTS.ORGANIZATIONS);
  return response.data;
}

/**
 * Get members of an organization.
 *
 * @param orgId - Organization ID
 * @param params - Optional pagination params (page, limit)
 * @returns List of organization members
 */
export async function getOrganizationMembers(
  orgId: string,
  params?: { limit?: number; page?: number },
): Promise<OrganizationMember[]> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.limit) queryParams.set('limit', String(params.limit));
  const queryString = queryParams.toString();
  const url = `${ENDPOINTS.ORGANIZATIONS}/${orgId}/members${queryString ? `?${queryString}` : ''}`;

  const response = await apiRequest<{ members: OrganizationMember[] }>(url);
  return response.data.members;
}

/**
 * Remove a member from an organization.
 * Admins and owners can remove members. Cannot remove last owner.
 *
 * @param orgId - Organization ID
 * @param userId - User ID of the member to remove
 * @throws ApiError with status 400 if removing last owner or self as owner
 * @throws ApiError with status 403 if not admin/owner
 */
export async function removeMember(orgId: string, userId: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.ORGANIZATIONS}/${orgId}/members/${userId}`, {
    method: HTTP_METHODS.DELETE,
  });
}

/**
 * Update a member's role.
 * Only organization owners can change roles.
 *
 * @param orgId - Organization ID
 * @param userId - User ID of the member
 * @param role - New role to assign
 * @returns Updated member info
 * @throws ApiError with status 403 if not owner
 */
export async function updateMemberRole(
  orgId: string,
  userId: string,
  role: OrgRole,
): Promise<OrganizationMember> {
  const response = await apiRequest<OrganizationMember>(
    `${ENDPOINTS.ORGANIZATIONS}/${orgId}/members/${userId}`,
    {
      method: HTTP_METHODS.PATCH,
      body: JSON.stringify({ role } satisfies UpdateRoleRequest),
    },
  );

  return response.data;
}

/**
 * Update an organization's details.
 * Requires owner or admin role.
 *
 * @param orgId - Organization ID
 * @param data - Fields to update (name, description)
 * @returns Updated organization
 */
export async function updateOrganization(
  orgId: string,
  data: UpdateOrganizationPayload,
): Promise<Organization> {
  const response = await apiRequest<Organization>(`${ENDPOINTS.ORGANIZATIONS}/${orgId}`, {
    method: HTTP_METHODS.PATCH,
    body: JSON.stringify(data),
  });

  return response.data;
}

const domainsBase = (orgId: string): string => `${ENDPOINTS.ORGANIZATIONS}/${orgId}/domains`;

export async function claimDomain(orgId: string, domain: string): Promise<OrgDomain> {
  const response = await apiRequest<OrgDomain>(domainsBase(orgId), {
    method: HTTP_METHODS.POST,
    body: JSON.stringify({ domain }),
  });
  return response.data;
}

export async function deleteDomain(orgId: string, domainId: string): Promise<void> {
  await apiRequest(`${domainsBase(orgId)}/${domainId}`, { method: HTTP_METHODS.DELETE });
}

export async function enrollExistingUsers(
  orgId: string,
  domainId: string,
  userUids?: string[],
): Promise<EnrollExistingUsersResponse> {
  const response = await apiRequest<EnrollExistingUsersResponse>(
    `${domainsBase(orgId)}/${domainId}/enroll`,
    {
      method: HTTP_METHODS.POST,
      body: userUids ? JSON.stringify({ user_uids: userUids }) : undefined,
    },
  );
  return response.data;
}

export async function listDomains(orgId: string): Promise<DomainListResponse> {
  const response = await apiRequest<DomainListResponse>(domainsBase(orgId), {
    method: HTTP_METHODS.GET,
  });
  return response.data;
}

export async function previewEnrollment(
  orgId: string,
  domainId: string,
  offset = 0,
  limit = 25,
): Promise<EnrollmentPreviewResponse> {
  const response = await apiRequest<EnrollmentPreviewResponse>(
    `${domainsBase(orgId)}/${domainId}/enrollment-preview?offset=${offset}&limit=${limit}`,
    { method: HTTP_METHODS.GET },
  );
  return response.data;
}

/**
 * Record an app-level login event so the auth log can show which app the user
 * authenticated into. Fire-and-forget — failures are silently swallowed so they
 * never block the sign-in flow.
 */
export async function recordLoginSession(): Promise<void> {
  try {
    // No body — the server derives the app from the Origin header, which the browser
    // sets automatically and JavaScript cannot override.
    await apiRequest('/auth/session', { method: HTTP_METHODS.POST });
  } catch {
    // Non-critical — never block the auth flow
  }
}

export async function triggerJitEnrollment(): Promise<void> {
  await apiRequest('/auth/domain-enrollment', { method: HTTP_METHODS.POST });
}

export async function updateGovernancePolicy(
  orgId: string,
  domainId: string,
  governance_policy: 'none' | 'auto_enroll',
): Promise<OrgDomain> {
  const response = await apiRequest<OrgDomain>(`${domainsBase(orgId)}/${domainId}/policy`, {
    method: HTTP_METHODS.PATCH,
    body: JSON.stringify({ governance_policy }),
  });
  return response.data;
}

export async function verifyDomain(orgId: string, domainId: string): Promise<VerifyDomainResponse> {
  const response = await apiRequest<VerifyDomainResponse>(
    `${domainsBase(orgId)}/${domainId}/verify`,
    { method: HTTP_METHODS.POST },
  );
  return response.data;
}
